import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
  Calendar, 
  Plus, 
  Clock, 
  Flag, 
  CheckCircle2, 
  AlertTriangle, 
  Users, 
  Edit3,
  Trash2,
  BarChart3,
  Target,
  ArrowRight,
  ArrowDown,
  ChevronRight,
  ChevronDown,
  Activity
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const GanttChart = ({ projectId, user }) => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [timeline, setTimeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('months'); // weeks, months, quarters
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  
  const [taskFormData, setTaskFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    assignee: '',
    priority: 'medium',
    progress: 0,
    parent_task_id: null,
    dependencies: []
  });

  const [milestoneFormData, setMilestoneFormData] = useState({
    name: '',
    description: '',
    target_date: '',
    status: 'pending'
  });

  // Approval workflow states
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null); // task or milestone
  const [selectedItemType, setSelectedItemType] = useState(null); // 'task' or 'milestone'
  const [completionNotes, setCompletionNotes] = useState('');
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [pendingApprovals, setPendingApprovals] = useState([]);

  // Edit/Delete workflow states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editComments, setEditComments] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditApprovalDialog, setShowEditApprovalDialog] = useState(false);
  const [editApprovalNotes, setEditApprovalNotes] = useState('');

  useEffect(() => {
    fetchTimelineData();
  }, [projectId]);

  const fetchTimelineData = async () => {
    try {
      const [tasksRes, milestonesRes, dependenciesRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/tasks`),
        axios.get(`${API}/projects/${projectId}/milestones`),
        axios.get(`${API}/projects/${projectId}/task-dependencies`)
      ]);
      
      setTasks(tasksRes.data);
      setMilestones(milestonesRes.data);
      setDependencies(dependenciesRes.data);
      
      // Filter items pending approval for project managers (both completion and edits)
      if (user?.role === 'project_manager' || user?.role === 'administrator') {
        const tasksPending = tasksRes.data.filter(t => 
          t.completion_status === 'pending_approval' || t.edit_status === 'pending_approval'
        );
        const milestonesPending = milestonesRes.data.filter(m => 
          m.completion_status === 'pending_approval' || m.edit_status === 'pending_approval'
        );
        setPendingApprovals([...tasksPending, ...milestonesPending]);
      }
      
      // Calculate timeline bounds
      const allDates = [
        ...tasksRes.data.map(t => new Date(t.start_date)),
        ...tasksRes.data.map(t => new Date(t.end_date)),
        ...milestonesRes.data.map(m => new Date(m.target_date))
      ];
      
      if (allDates.length > 0) {
        const minDate = new Date(Math.min(...allDates));
        const maxDate = new Date(Math.max(...allDates));
        setTimeline({ start: minDate, end: maxDate });
      }
    } catch (error) {
      toast.error(t('gantt_chart.load_failed'));
      console.error('Timeline fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCompletion = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedItemType === 'task' 
        ? `${API}/projects/${projectId}/tasks/${selectedItem.id}/submit-completion`
        : `${API}/projects/${projectId}/milestones/${selectedItem.id}/submit-completion`;
      
      await axios.post(endpoint, {
        notes: completionNotes,
        evidence: '' // Could add file upload later
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`${selectedItemType === 'task' ? 'Task' : 'Milestone'} completion submitted for approval`);
      setShowCompletionDialog(false);
      setCompletionNotes('');
      setSelectedItem(null);
      setSelectedItemType(null);
      fetchTimelineData(); // Refresh data
    } catch (error) {
      console.error('Failed to submit completion:', error);
      toast.error('Failed to submit completion');
    }
  };

  const handleApproveCompletion = async (approved) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedItemType === 'task' 
        ? `${API}/projects/${projectId}/tasks/${selectedItem.id}/approve-completion`
        : `${API}/projects/${projectId}/milestones/${selectedItem.id}/approve-completion`;
      
      await axios.post(endpoint, {
        approved: approved,
        notes: approvalNotes
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`${selectedItemType === 'task' ? 'Task' : 'Milestone'} completion ${approved ? 'approved' : 'rejected'}`);
      setShowApprovalDialog(false);
      setApprovalNotes('');
      setSelectedItem(null);
      setSelectedItemType(null);
      fetchTimelineData(); // Refresh data
    } catch (error) {
      console.error('Failed to approve completion:', error);
      toast.error('Failed to process approval');
    }
  };

  const openCompletionDialog = (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setShowCompletionDialog(true);
  };

  const openApprovalDialog = (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setShowApprovalDialog(true);
  };

  // Edit/Delete handlers
  const openEditDialog = (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setEditComments('');
    
    // Pre-fill form data based on type
    if (type === 'task') {
      setTaskFormData({
        name: item.name || '',
        description: item.description || '',
        start_date: item.start_date ? item.start_date.split('T')[0] : '',
        end_date: item.end_date ? item.end_date.split('T')[0] : '',
        assignee: item.assignee || '',
        priority: item.priority || 'medium',
        progress: item.progress || 0,
        parent_task_id: item.parent_task_id || null,
        dependencies: item.dependencies || []
      });
    } else {
      setMilestoneFormData({
        name: item.name || '',
        description: item.description || '',
        target_date: item.target_date ? item.target_date.split('T')[0] : '',
        status: item.status || 'pending'
      });
    }
    
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const formData = selectedItemType === 'task' ? taskFormData : milestoneFormData;
      
      // Add comments to the update payload
      const updateData = {
        ...formData,
        edit_comments: editComments
      };
      
      const endpoint = selectedItemType === 'task' 
        ? `${API}/projects/${projectId}/tasks/${selectedItem.id}`
        : `${API}/projects/${projectId}/milestones/${selectedItem.id}`;
      
      await axios.put(endpoint, updateData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // If user is social_actor, edits need approval
      if (user?.role === 'social_actor') {
        toast.success(`${selectedItemType === 'task' ? 'Task' : 'Milestone'} edit submitted for approval`);
      } else {
        toast.success(`${selectedItemType === 'task' ? 'Task' : 'Milestone'} updated successfully`);
      }
      
      setShowEditDialog(false);
      setEditComments('');
      setSelectedItem(null);
      setSelectedItemType(null);
      fetchTimelineData(); // Refresh data
    } catch (error) {
      console.error('Failed to update:', error);
      const errorMsg = error.response?.data?.detail || 
                       (Array.isArray(error.response?.data) ? error.response.data[0]?.msg : null) ||
                       error.message || 
                       `Failed to update ${selectedItemType}`;
      toast.error(errorMsg);
    }
  };

  const openDeleteDialog = (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedItemType === 'task' 
        ? `${API}/projects/${projectId}/tasks/${selectedItem.id}`
        : `${API}/projects/${projectId}/milestones/${selectedItem.id}`;
      
      await axios.delete(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`${selectedItemType === 'task' ? 'Task' : 'Milestone'} deleted successfully`);
      setShowDeleteDialog(false);
      setSelectedItem(null);
      setSelectedItemType(null);
      fetchTimelineData(); // Refresh data
    } catch (error) {
      console.error('Failed to delete:', error);
      const errorMsg = error.response?.data?.detail || 
                       (Array.isArray(error.response?.data) ? error.response.data[0]?.msg : null) ||
                       error.message || 
                       `Failed to delete ${selectedItemType}`;
      toast.error(errorMsg);
    }
  };

  // Edit Approval handlers
  const openEditApprovalDialog = (item, type) => {
    setSelectedItem(item);
    setSelectedItemType(type);
    setEditApprovalNotes('');
    setShowEditApprovalDialog(true);
  };

  const handleApproveEdit = async (approved) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedItemType === 'task' 
        ? `${API}/projects/${projectId}/tasks/${selectedItem.id}/approve-edit`
        : `${API}/projects/${projectId}/milestones/${selectedItem.id}/approve-edit`;
      
      await axios.post(endpoint, {
        approved: approved,
        notes: editApprovalNotes
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`${selectedItemType === 'task' ? 'Task' : 'Milestone'} edit ${approved ? 'approved' : 'rejected'}`);
      setShowEditApprovalDialog(false);
      setEditApprovalNotes('');
      setSelectedItem(null);
      setSelectedItemType(null);
      fetchTimelineData(); // Refresh data
    } catch (error) {
      console.error('Failed to process edit approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process edit approval');
    }
  };

  // Check if user can edit (Social Actor, Project Manager, or Administrator)
  const canEdit = () => {
    return user?.role === 'social_actor' || user?.role === 'project_manager' || user?.role === 'administrator';
  };

  const getCompletionStatusBadge = (item) => {
    const status = item.completion_status || 'in_progress';
    switch (status) {
      case 'pending_approval':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Pending PM Approval</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700">Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-600">Rejected</Badge>;
      default:
        return null;
    }
  };

  const getEditStatusBadge = (item) => {
    if (item.edit_status === 'pending_approval') {
      return <Badge variant="outline" className="bg-purple-50 text-purple-600">Edit Pending</Badge>;
    }
    return null;
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const taskData = {
        ...taskFormData,
        start_date: new Date(taskFormData.start_date).toISOString(),
        end_date: new Date(taskFormData.end_date).toISOString(),
        progress: parseInt(taskFormData.progress)
      };

      await axios.post(`${API}/projects/${projectId}/tasks`, taskData);
      toast.success('Task created successfully!');
      setShowTaskDialog(false);
      setTaskFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        assignee: '',
        priority: 'medium',
        progress: 0,
        parent_task_id: null,
        dependencies: []
      });
      fetchTimelineData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    try {
      const milestoneData = {
        ...milestoneFormData,
        target_date: new Date(milestoneFormData.target_date).toISOString()
      };

      await axios.post(`${API}/projects/${projectId}/milestones`, milestoneData);
      toast.success('Milestone created successfully!');
      setShowMilestoneDialog(false);
      setMilestoneFormData({
        name: '',
        description: '',
        target_date: '',
        status: 'pending'
      });
      fetchTimelineData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create milestone');
    }
  };

  const updateTaskProgress = async (taskId, progress) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/tasks/${taskId}`, { progress });
      setTasks(tasks.map(task => 
        task.id === taskId ? { ...task, progress } : task
      ));
      toast.success('Task progress updated');
    } catch (error) {
      toast.error('Failed to update task progress');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-600';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-700';
      case 'low': return 'bg-green-700';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateTaskWidth = (startDate, endDate) => {
    if (!timeline.start || !timeline.end) return 0;
    
    const totalDuration = timeline.end.getTime() - timeline.start.getTime();
    const taskStart = new Date(startDate).getTime();
    const taskEnd = new Date(endDate).getTime();
    const taskDuration = taskEnd - taskStart;
    
    return Math.max((taskDuration / totalDuration) * 100, 2); // Minimum 2% width
  };

  const calculateTaskOffset = (startDate) => {
    if (!timeline.start || !timeline.end) return 0;
    
    const totalDuration = timeline.end.getTime() - timeline.start.getTime();
    const taskStart = new Date(startDate).getTime();
    const offset = taskStart - timeline.start.getTime();
    
    return (offset / totalDuration) * 100;
  };

  const generateTimelineHeader = () => {
    if (!timeline.start || !timeline.end) return [];
    
    const periods = [];
    const current = new Date(timeline.start);
    current.setDate(1); // Start from first day of month
    
    while (current <= timeline.end) {
      periods.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }
    
    return periods;
  };

  const toggleTaskExpansion = (taskId) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  const getTaskHierarchy = () => {
    const parentTasks = tasks.filter(task => !task.parent_task_id);
    const childTasks = tasks.filter(task => task.parent_task_id);
    
    return parentTasks.map(parent => ({
      ...parent,
      children: childTasks.filter(child => child.parent_task_id === parent.id)
    }));
  };

  const TaskRow = ({ task, level = 0, isChild = false }) => {
    const hasChildren = task.children && task.children.length > 0;
    const isExpanded = expandedTasks.has(task.id);
    const taskWidth = calculateTaskWidth(task.start_date, task.end_date);
    const taskOffset = calculateTaskOffset(task.start_date);
    
    return (
      <>
        <div className={`border-b border-gray-200 ${isChild ? 'bg-gray-50' : 'bg-white'}`}>
          <div className="grid grid-cols-12 gap-4 p-3 items-center">
            {/* Task Name Column */}
            <div className="col-span-4" style={{ paddingLeft: `${level * 20}px` }}>
              <div className="flex items-center space-x-2">
                {hasChildren && (
                  <button 
                    onClick={() => toggleTaskExpansion(task.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                )}
                <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`}></div>
                <div>
                  <p className="font-medium text-sm">{task.name}</p>
                  <p className="text-xs text-gray-500">{task.assignee}</p>
                </div>
              </div>
            </div>
            
            {/* Dates Column */}
            <div className="col-span-2 text-xs text-gray-600">
              <div>{new Date(task.start_date).toLocaleDateString('en-GB')}</div>
              <div>{new Date(task.end_date).toLocaleDateString('en-GB')}</div>
            </div>
            
            {/* Status Column */}
            <div className="col-span-2">
              <Badge className={getStatusColor(task.status)}>
                {task.status.replace('_', ' ')}
              </Badge>
            </div>
            
            {/* Progress Column */}
            <div className="col-span-2">
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span>Progress</span>
                  <span>{task.progress}%</span>
                </div>
                <Progress value={task.progress} className="h-2" />
              </div>
            </div>
            
            {/* Timeline Visualization Column */}
            <div className="col-span-2 relative h-8">
              <div 
                className={`absolute top-1 h-6 rounded ${task.progress >= 100 ? 'bg-green-700' : 'bg-blue-500'} opacity-80`}
                style={{
                  left: `${taskOffset}%`,
                  width: `${taskWidth}%`
                }}
              >
                <div 
                  className="h-full bg-green-700 rounded"
                  style={{ width: `${task.progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Child Tasks */}
        {hasChildren && isExpanded && task.children.map(child => (
          <TaskRow 
            key={child.id} 
            task={child} 
            level={level + 1} 
            isChild={true}
          />
        ))}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const timelineHeaders = generateTimelineHeader();
  const taskHierarchy = getTaskHierarchy();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <span>{t('gantt_chart.header_title')}</span>
          </h3>
          <p className="text-sm text-gray-600">{t('gantt_chart.header_subtitle')}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weeks">{t('gantt_chart.view_weeks')}</SelectItem>
              <SelectItem value="months">{t('gantt_chart.view_months')}</SelectItem>
              <SelectItem value="quarters">{t('gantt_chart.view_quarters')}</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-milestone-button">
                <Flag className="h-4 w-4 mr-2" />
                {t('gantt_chart.add_milestone')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Milestone</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateMilestone} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="milestone-name">Milestone Name</Label>
                  <Input
                    id="milestone-name"
                    value={milestoneFormData.name}
                    onChange={(e) => setMilestoneFormData({...milestoneFormData, name: e.target.value})}
                    placeholder="e.g., Project Kickoff"
                    required
                    data-testid="milestone-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="milestone-description">Description</Label>
                  <Textarea
                    id="milestone-description"
                    value={milestoneFormData.description}
                    onChange={(e) => setMilestoneFormData({...milestoneFormData, description: e.target.value})}
                    placeholder="Describe the milestone..."
                    rows={3}
                    data-testid="milestone-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="milestone-date">Target Date</Label>
                  <Input
                    id="milestone-date"
                    type="date"
                    value={milestoneFormData.target_date}
                    onChange={(e) => setMilestoneFormData({...milestoneFormData, target_date: e.target.value})}
                    required
                    data-testid="milestone-date-input"
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowMilestoneDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="create-milestone-submit">Create Milestone</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-task-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Task</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="task-name">Task Name</Label>
                    <Input
                      id="task-name"
                      value={taskFormData.name}
                      onChange={(e) => setTaskFormData({...taskFormData, name: e.target.value})}
                      placeholder="e.g., Requirements Gathering"
                      required
                      data-testid="task-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="task-assignee">Assignee</Label>
                    <Input
                      id="task-assignee"
                      value={taskFormData.assignee}
                      onChange={(e) => setTaskFormData({...taskFormData, assignee: e.target.value})}
                      placeholder="e.g., John Doe"
                      data-testid="task-assignee-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task-description">Description</Label>
                  <Textarea
                    id="task-description"
                    value={taskFormData.description}
                    onChange={(e) => setTaskFormData({...taskFormData, description: e.target.value})}
                    placeholder="Describe the task..."
                    rows={3}
                    data-testid="task-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={taskFormData.start_date}
                      onChange={(e) => setTaskFormData({...taskFormData, start_date: e.target.value})}
                      required
                      data-testid="task-start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={taskFormData.end_date}
                      onChange={(e) => setTaskFormData({...taskFormData, end_date: e.target.value})}
                      required
                      data-testid="task-end-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="task-priority">Priority</Label>
                    <Select value={taskFormData.priority} onValueChange={(value) => setTaskFormData({...taskFormData, priority: value})}>
                      <SelectTrigger data-testid="task-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task-progress">Progress (%)</Label>
                  <Input
                    id="task-progress"
                    type="number"
                    min="0"
                    max="100"
                    value={taskFormData.progress}
                    onChange={(e) => setTaskFormData({...taskFormData, progress: e.target.value})}
                    data-testid="task-progress-input"
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowTaskDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="create-task-submit">Create Task</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Timeline Header */}
      {timelineHeaders.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-4 border-b pb-2 mb-4">
              <div className="col-span-4 font-medium">Task</div>
              <div className="col-span-2 font-medium">Dates</div>
              <div className="col-span-2 font-medium">Status</div>
              <div className="col-span-2 font-medium">Progress</div>
              <div className="col-span-2 font-medium">Timeline</div>
            </div>
            
            {/* Time Scale */}
            <div className="grid grid-cols-12 gap-4 mb-4">
              <div className="col-span-8"></div>
              <div className="col-span-4">
                <div className="flex justify-between text-xs text-gray-500 border-b pb-1">
                  {timelineHeaders.map((date, index) => (
                    <span key={index}>
                      {date.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' })}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks List */}
      <Card>
        <CardContent className="p-0">
          {taskHierarchy.length > 0 ? (
            taskHierarchy.map(task => (
              <TaskRow key={task.id} task={task} />
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No tasks defined yet</p>
              <p className="text-sm">Add tasks to start building your project timeline</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Tasks */}
      {tasks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-blue-600" />
              <span>{t('gantt_chart.project_tasks')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.map((task) => (
                <Card key={task.id} className="border-l-4 border-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium">{task.name}</h4>
                      <div className="flex flex-col gap-1">
                        <Badge className={getStatusColor(task.status)}>
                          {task.status}
                        </Badge>
                        {getCompletionStatusBadge(task)}
                        {getEditStatusBadge(task)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                    
                    {task.approval_notes && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <p className="font-medium text-green-900">{t('gantt_chart.pm_approval_notes')}</p>
                        <p className="text-gray-700">{task.approval_notes}</p>
                      </div>
                    )}
                    
                    {task.rejection_notes && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
                        <p className="font-medium text-red-900">{t('gantt_chart.pm_rejection_notes')}</p>
                        <p className="text-gray-700">{task.rejection_notes}</p>
                      </div>
                    )}
                    
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{task.progress}%</span>
                      </div>
                      <Progress value={task.progress} className="h-2" />
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(task.start_date).toLocaleDateString('en-GB')}</span>
                      <ArrowRight className="h-3 w-3" />
                      <span>{new Date(task.end_date).toLocaleDateString('en-GB')}</span>
                    </div>
                    
                    {/* Action buttons integrated within the card */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex gap-2">
                        {/* Workflow action buttons based on user role and task status */}
                        {user?.role === 'social_actor' && task.completion_status === 'in_progress' && !task.edit_status && (
                          <Button 
                            size="sm" 
                            onClick={() => openCompletionDialog(task, 'task')}
                            className="bg-green-700 hover:bg-green-800 text-white"
                          >
                            Mark Complete
                          </Button>
                        )}
                        
                        {(user?.role === 'project_manager' || user?.role === 'administrator') && task.completion_status === 'pending_approval' && (
                          <Button 
                            size="sm" 
                            onClick={() => openApprovalDialog(task, 'task')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Review Completion
                          </Button>
                        )}
                        
                        {(user?.role === 'project_manager' || user?.role === 'administrator') && task.edit_status === 'pending_approval' && (
                          <Button 
                            size="sm" 
                            onClick={() => openEditApprovalDialog(task, 'task')}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Review Edit
                          </Button>
                        )}
                      </div>
                      
                      {/* Edit/Delete/Comments action buttons */}
                      {canEdit() && (
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Edit Task"
                            onClick={() => openEditDialog(task, 'task')}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            title="Delete Task"
                            onClick={() => openDeleteDialog(task, 'task')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Milestones */}
      {milestones.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Flag className="h-5 w-5 text-purple-600" />
              <span>{t('gantt_chart.project_milestones')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {milestones.map((milestone) => (
                <Card key={milestone.id} className="border-l-4 border-purple-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium">{milestone.name}</h4>
                      <div className="flex flex-col gap-1">
                        <Badge className={getStatusColor(milestone.status)}>
                          {milestone.status}
                        </Badge>
                        {getCompletionStatusBadge(milestone)}
                        {getEditStatusBadge(milestone)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{milestone.description}</p>
                    
                    {milestone.approval_notes && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <p className="font-medium text-green-900">{t('gantt_chart.pm_approval_notes')}</p>
                        <p className="text-gray-700">{milestone.approval_notes}</p>
                      </div>
                    )}
                    
                    {milestone.rejection_notes && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
                        <p className="font-medium text-red-900">{t('gantt_chart.pm_rejection_notes')}</p>
                        <p className="text-gray-700">{milestone.rejection_notes}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(milestone.target_date).toLocaleDateString('en-GB')}</span>
                    </div>
                    
                    {/* Action buttons integrated within the card */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex gap-2">
                        {/* Workflow action buttons based on user role and milestone status */}
                        {user?.role === 'social_actor' && milestone.completion_status === 'in_progress' && !milestone.edit_status && (
                          <Button 
                            size="sm" 
                            onClick={() => openCompletionDialog(milestone, 'milestone')}
                            className="bg-green-700 hover:bg-green-800 text-white"
                          >
                            Mark Complete
                          </Button>
                        )}
                        
                        {(user?.role === 'project_manager' || user?.role === 'administrator') && milestone.completion_status === 'pending_approval' && (
                          <Button 
                            size="sm" 
                            onClick={() => openApprovalDialog(milestone, 'milestone')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Review Completion
                          </Button>
                        )}
                        
                        {(user?.role === 'project_manager' || user?.role === 'administrator') && milestone.edit_status === 'pending_approval' && (
                          <Button 
                            size="sm" 
                            onClick={() => openEditApprovalDialog(milestone, 'milestone')}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            Review Edit
                          </Button>
                        )}
                      </div>
                      
                      {/* Edit/Delete/Comments action buttons */}
                      {canEdit() && (
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Edit Milestone"
                            onClick={() => openEditDialog(milestone, 'milestone')}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            title="Delete Milestone"
                            onClick={() => openDeleteDialog(milestone, 'milestone')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Critical Path Analysis moved to Reports tab */}

      {/* Completion Submission Dialog (Social Actor) */}
      <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Submit {selectedItemType === 'task' ? 'Task' : 'Milestone'} Completion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="font-medium">{selectedItem?.name}</p>
              <p className="text-sm text-gray-600">{selectedItem?.description}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="completion-notes">Completion Notes</Label>
              <Textarea
                id="completion-notes"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Provide details about the completion, any challenges faced, or additional information..."
                rows={4}
              />
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This will mark the {selectedItemType} as "Pending PM Approval" and notify the Project Manager for review.
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCompletionDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitCompletion}
                className="bg-green-700 hover:bg-green-800 text-white"
              >
                Submit for Approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog (Project Manager) */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Review {selectedItemType === 'task' ? 'Task' : 'Milestone'} Completion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="font-medium">{selectedItem?.name}</p>
              <p className="text-sm text-gray-600">{selectedItem?.description}</p>
            </div>
            {selectedItem?.completion_notes && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-sm text-blue-800">Social Actor Notes:</p>
                <p className="text-sm text-blue-700">{selectedItem.completion_notes}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="approval-notes">Review Notes</Label>
              <Textarea
                id="approval-notes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Provide feedback or additional comments..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowApprovalDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => handleApproveCompletion(false)}
              >
                Reject
              </Button>
              <Button 
                onClick={() => handleApproveCompletion(true)}
                className="bg-green-700 hover:bg-green-800 text-white"
              >
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit {selectedItemType === 'task' ? 'Task' : 'Milestone'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            {selectedItemType === 'task' ? (
              // Task Edit Form
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-name">Task Name</Label>
                    <Input
                      id="edit-task-name"
                      value={taskFormData.name}
                      onChange={(e) => setTaskFormData({...taskFormData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-assignee">Assignee</Label>
                    <Input
                      id="edit-task-assignee"
                      value={taskFormData.assignee}
                      onChange={(e) => setTaskFormData({...taskFormData, assignee: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-task-description">Description</Label>
                  <Textarea
                    id="edit-task-description"
                    value={taskFormData.description}
                    onChange={(e) => setTaskFormData({...taskFormData, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-start-date">Start Date</Label>
                    <Input
                      id="edit-start-date"
                      type="date"
                      value={taskFormData.start_date}
                      onChange={(e) => setTaskFormData({...taskFormData, start_date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-end-date">End Date</Label>
                    <Input
                      id="edit-end-date"
                      type="date"
                      value={taskFormData.end_date}
                      onChange={(e) => setTaskFormData({...taskFormData, end_date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-task-priority">Priority</Label>
                    <Select value={taskFormData.priority} onValueChange={(value) => setTaskFormData({...taskFormData, priority: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-task-progress">Progress (%)</Label>
                  <Input
                    id="edit-task-progress"
                    type="number"
                    min="0"
                    max="100"
                    value={taskFormData.progress}
                    onChange={(e) => setTaskFormData({...taskFormData, progress: parseInt(e.target.value)})}
                  />
                </div>
              </>
            ) : (
              // Milestone Edit Form
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-milestone-name">Milestone Name</Label>
                  <Input
                    id="edit-milestone-name"
                    value={milestoneFormData.name}
                    onChange={(e) => setMilestoneFormData({...milestoneFormData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-milestone-description">Description</Label>
                  <Textarea
                    id="edit-milestone-description"
                    value={milestoneFormData.description}
                    onChange={(e) => setMilestoneFormData({...milestoneFormData, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-milestone-date">Target Date</Label>
                  <Input
                    id="edit-milestone-date"
                    type="date"
                    value={milestoneFormData.target_date}
                    onChange={(e) => setMilestoneFormData({...milestoneFormData, target_date: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-milestone-status">Status</Label>
                  <Select value={milestoneFormData.status} onValueChange={(value) => setMilestoneFormData({...milestoneFormData, status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {/* Comments Section */}
            <div className="space-y-2">
              <Label htmlFor="edit-comments">Edit Comments / Notes</Label>
              <Textarea
                id="edit-comments"
                value={editComments}
                onChange={(e) => setEditComments(e.target.value)}
                placeholder="Provide details about your changes..."
                rows={3}
              />
            </div>

            {user?.role === 'social_actor' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> As a Social Actor, your edits will be submitted for approval by the Project Manager or Administrator.
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {user?.role === 'social_actor' ? 'Submit for Approval' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Delete {selectedItemType === 'task' ? 'Task' : 'Milestone'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This action cannot be undone. Are you sure you want to delete this {selectedItemType}?
              </p>
            </div>
            <div>
              <p className="font-medium">{selectedItem?.name}</p>
              <p className="text-sm text-gray-600">{selectedItem?.description}</p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete {selectedItemType === 'task' ? 'Task' : 'Milestone'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Approval Dialog (Project Manager/Administrator) */}
      <Dialog open={showEditApprovalDialog} onOpenChange={setShowEditApprovalDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              Review {selectedItemType === 'task' ? 'Task' : 'Milestone'} Edit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-800">
                <strong>Pending Edit:</strong> A Social Actor has submitted changes to this {selectedItemType} for your review.
              </p>
            </div>
            
            {selectedItem?.pending_changes && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="font-medium text-red-900 mb-3">Current Information</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Name:</span>
                      <p className="text-gray-700">{selectedItem.name}</p>
                    </div>
                    <div>
                      <span className="font-medium">Description:</span>
                      <p className="text-gray-700">{selectedItem.description}</p>
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>
                      <p className="text-gray-700">{selectedItem.status}</p>
                    </div>
                    {selectedItemType === 'task' ? (
                      <>
                        <div>
                          <span className="font-medium">Start Date:</span>
                          <p className="text-gray-700">{new Date(selectedItem.start_date).toLocaleDateString('en-GB')}</p>
                        </div>
                        <div>
                          <span className="font-medium">End Date:</span>
                          <p className="text-gray-700">{new Date(selectedItem.end_date).toLocaleDateString('en-GB')}</p>
                        </div>
                        <div>
                          <span className="font-medium">Progress:</span>
                          <p className="text-gray-700">{selectedItem.progress}%</p>
                        </div>
                      </>
                    ) : (
                      <div>
                        <span className="font-medium">Target Date:</span>
                        <p className="text-gray-700">{new Date(selectedItem.target_date).toLocaleDateString('en-GB')}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-900 mb-3">Proposed Changes</h4>
                  <div className="space-y-2 text-sm">
                    {selectedItem.pending_changes.name && selectedItem.pending_changes.name !== selectedItem.name && (
                      <div>
                        <span className="font-medium">Name:</span>
                        <p className="text-gray-700">{selectedItem.pending_changes.name}</p>
                      </div>
                    )}
                    {selectedItem.pending_changes.description && selectedItem.pending_changes.description !== selectedItem.description && (
                      <div>
                        <span className="font-medium">Description:</span>
                        <p className="text-gray-700">{selectedItem.pending_changes.description}</p>
                      </div>
                    )}
                    {selectedItem.pending_changes.status && selectedItem.pending_changes.status !== selectedItem.status && (
                      <div>
                        <span className="font-medium">Status:</span>
                        <p className="text-gray-700">{selectedItem.pending_changes.status}</p>
                      </div>
                    )}
                    {selectedItemType === 'task' ? (
                      <>
                        {selectedItem.pending_changes.start_date && selectedItem.pending_changes.start_date !== selectedItem.start_date && (
                          <div>
                            <span className="font-medium">Start Date:</span>
                            <p className="text-gray-700">{new Date(selectedItem.pending_changes.start_date).toLocaleDateString('en-GB')}</p>
                          </div>
                        )}
                        {selectedItem.pending_changes.end_date && selectedItem.pending_changes.end_date !== selectedItem.end_date && (
                          <div>
                            <span className="font-medium">End Date:</span>
                            <p className="text-gray-700">{new Date(selectedItem.pending_changes.end_date).toLocaleDateString('en-GB')}</p>
                          </div>
                        )}
                        {selectedItem.pending_changes.progress !== undefined && selectedItem.pending_changes.progress !== selectedItem.progress && (
                          <div>
                            <span className="font-medium">Progress:</span>
                            <p className="text-gray-700">{selectedItem.pending_changes.progress}%</p>
                          </div>
                        )}
                      </>
                    ) : (
                      selectedItem.pending_changes.target_date && selectedItem.pending_changes.target_date !== selectedItem.target_date && (
                        <div>
                          <span className="font-medium">Target Date:</span>
                          <p className="text-gray-700">{new Date(selectedItem.pending_changes.target_date).toLocaleDateString('en-GB')}</p>
                        </div>
                      )
                    )}
                    {!Object.keys(selectedItem.pending_changes).some(key => 
                      key !== 'edit_comments' && key !== 'edit_status' && selectedItem.pending_changes[key] !== selectedItem[key]
                    ) && (
                      <p className="text-gray-500 italic">No visible changes detected</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {selectedItem?.pending_changes?.edit_comments && (
              <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-sm font-medium text-blue-900">Social Actor's Comments:</p>
                <p className="text-sm text-gray-700 mt-1">{selectedItem.pending_changes.edit_comments}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="edit-approval-notes">Review Notes</Label>
              <Textarea
                id="edit-approval-notes"
                value={editApprovalNotes}
                onChange={(e) => setEditApprovalNotes(e.target.value)}
                placeholder="Add your review notes..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowEditApprovalDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleApproveEdit(false)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Reject
              </Button>
              <Button 
                onClick={() => handleApproveEdit(true)}
                className="bg-green-700 hover:bg-green-800 text-white"
              >
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GanttChart;